// SGNL-213 — Tab-scoped probability-preset state machine factory.
//
// Three tabs (Progress / Lost Deal Analysis / Revenue Projection) now
// run the same dropdown: 4 presets {All, Qualified+, Mid-stage+,
// Late-stage+}, default ≥5%, persisted in sessionStorage under a
// per-tab namespace key, with the same isValidPreset + read/write +
// reset surface.
//
// Architect MED on the SGNL-214 pre-PR review flagged the duplication
// across views/weekly_call.ts + views/lost.ts. The retrofit of those
// two existing modules was deferred per the review's "don't widen
// SGNL-214's scope" note — they keep their local state machines for
// now. NEW code (SGNL-213) uses this shared factory; the retrofit is
// a future-ticket follow-up.

export interface PresetDef<V extends string> {
  /** Internal slug stored in sessionStorage + the <select> value. */
  readonly value: V;
  /** Display label rendered in the <option>. */
  readonly label: string;
  /** Minimum probability threshold. `null` = "no filter". */
  readonly min: number | null;
}

export interface TabbedPreset<V extends string> {
  /** Read-only list of presets in render order. */
  readonly presets: ReadonlyArray<PresetDef<V>>;
  /** Current preset value (cached + sessionStorage-backed). */
  get(): V;
  /** Set the preset; persists to sessionStorage. Silently no-ops if
   *  the value isn't in the preset list. */
  set(value: string): void;
  /** Return the `min` threshold for the active preset. */
  getMin(): number | null;
  /** Test-only seam — reset to the default + clear sessionStorage. */
  resetForTests(): void;
}

/** Build a fresh tab-scoped preset state machine.
 *
 * Each call returns an independent `{get, set, getMin, resetForTests}`
 * surface backed by its own sessionStorage key. The factory enforces
 * runtime validation of the persisted value (so a stale / hand-tampered
 * key falls back to the default) and exposes a `resetForTests` seam
 * matching the per-tab modules' prior pattern.
 *
 * `defaultValue` MUST appear in `presets[].value`; this is enforced by
 * a runtime guard so a future contributor adding a typo'd default
 * fails loudly at module load. */
// Slugs whose names collide with Object.prototype properties. The
// factory rejects these even if a future caller adds them to the
// presets list — `_value = "__proto__"` is harmless on its own but
// signals a misuse pattern that's better caught early (security MED
// pre-PR review).
const FORBIDDEN_SLUGS: ReadonlySet<string> = new Set([
  "__proto__",
  "constructor",
  "prototype",
]);

export function createTabbedPreset<V extends string>(opts: {
  presets: ReadonlyArray<PresetDef<V>>;
  defaultValue: V;
  sessionKey: string;
}): TabbedPreset<V> {
  // Architect LOW pre-PR review: shallow-copy so a future mutable
  // caller that splices its local array doesn't silently invalidate
  // `isValid`'s view. The copy is structurally identical (same
  // PresetDef references) and ReadonlyArray-typed.
  const presets: ReadonlyArray<PresetDef<V>> = [...opts.presets];
  const { defaultValue, sessionKey } = opts;
  if (!presets.some((p) => p.value === defaultValue)) {
    throw new Error(
      `[tabbed_preset] defaultValue "${defaultValue}" not found in presets for key "${sessionKey}"`,
    );
  }

  function isValid(v: unknown): v is V {
    if (typeof v !== "string") return false;
    // Security MED pre-PR review: reject prototype-name slugs so a
    // future caller can't register a preset value of "__proto__" that
    // would later be assigned to `_value` and surface in template
    // interpolations or DOM attribute writes.
    if (FORBIDDEN_SLUGS.has(v)) return false;
    return presets.some((p) => p.value === v);
  }

  function readPersisted(): V {
    try {
      const v = sessionStorage.getItem(sessionKey);
      if (isValid(v)) return v;
    } catch {
      // Safari Private Browsing pre-iOS 17 throws on any
      // sessionStorage access — silent fallback to default.
    }
    return defaultValue;
  }

  // Lazy initialisation — read once on first `get()`. Avoids hitting
  // sessionStorage during module-import time (which trips some test
  // environments that don't mount jsdom early enough).
  let _value: V | null = null;

  function get(): V {
    if (_value === null) _value = readPersisted();
    return _value;
  }

  function set(value: string): void {
    if (!isValid(value)) return;
    _value = value;
    try {
      sessionStorage.setItem(sessionKey, value);
    } catch {
      // same Safari guard
    }
  }

  function getMin(): number | null {
    return presets.find((p) => p.value === get())?.min ?? null;
  }

  function resetForTests(): void {
    _value = defaultValue;
    try {
      sessionStorage.removeItem(sessionKey);
    } catch {
      // ignore
    }
  }

  return { presets, get, set, getMin, resetForTests };
}

/** Shared probability-preset set used by Progress / Lost / Revenue
 *  Projection. Kept as a single constant so the four presets stay in
 *  sync across the three tabs. */
export const STANDARD_PROBABILITY_PRESETS = [
  { value: "all", label: "All probabilities", min: null },
  { value: "q5",  label: "Qualified+ (≥5%)",  min: 5 },
  { value: "m30", label: "Mid-stage+ (≥30%)", min: 30 },
  { value: "l70", label: "Late-stage+ (≥70%)", min: 70 },
] as const satisfies ReadonlyArray<PresetDef<string>>;

export type StandardProbabilityPresetValue =
  (typeof STANDARD_PROBABILITY_PRESETS)[number]["value"];
